import apiClient from '../client';

// Types
export interface AlertsDashboard {
  success: boolean;
  dashboard: {
    period_days: number;
    alerts: {
      total: number;
      active: number;
      acknowledged: number;
      resolved: number;
      critical: number;
      high: number;
      medium: number;
      low: number;
    };
    alert_rules: {
      total: number;
      enabled: number;
      disabled: number;
      triggered_today: number;
    };
    categories: {
      [key: string]: number;
    };
    response_metrics: {
      average_response_time_minutes: number;
      average_resolution_time_hours: number;
      acknowledgement_rate: number;
    };
  };
}

export interface Alert {
  id: number;
  alert_id: string;
  rule_id: string;
  rule_name: string;
  category: string;
  severity: string;
  status: string;
  title: string;
  message: string;
  triggered_at: string;
  acknowledged_at?: string;
  acknowledged_by?: string;
  resolved_at?: string;
  resolved_by?: string;
  alert_data: any;
  threshold_value?: number;
  actual_value?: number;
  notification_sent: boolean;
}

export interface AlertRule {
  id: number;
  rule_id: string;
  rule_name: string;
  description?: string;
  category: string;
  severity: string;
  condition_type: string;
  condition_config: any;
  threshold_value?: number;
  threshold_operator?: string;
  enabled: boolean;
  notification_channels: string[];
  notification_template?: string;
  cooldown_minutes: number;
  last_triggered_at?: string;
  trigger_count: number;
  created_at: string;
}

// API Functions
export const alertsService = {
  // Get alerts dashboard
  getDashboard: async (days: number = 30): Promise<AlertsDashboard> => {
    const response = await apiClient.get(`/alerts/dashboard?days=${days}`);
    return response.data;
  },

  // Get alerts (uses /active endpoint for active alerts)
  getAlerts: async (params?: {
    status?: string;
    severity?: string;
    category?: string;
    rule_id?: string;
    start_date?: string;
    end_date?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ alerts: Alert[]; total: number }> => {
    // Use /active for active alerts, /history for historical
    const endpoint = params?.status === 'resolved' ? '/alerts/history' : '/alerts/active';
    const response = await apiClient.get(endpoint, { params });
    return response.data;
  },

  // Get alert by ID
  getAlert: async (alertId: string): Promise<{ success: boolean; alert: Alert }> => {
    const response = await apiClient.get(`/alerts/${alertId}`);
    return response.data;
  },

  // Acknowledge alert
  acknowledgeAlert: async (alertId: string, notes?: string): Promise<{ success: boolean; alert: Alert }> => {
    const response = await apiClient.post(`/alerts/${alertId}/acknowledge`, { notes });
    return response.data;
  },

  // Resolve alert
  resolveAlert: async (alertId: string, data: {
    resolution_notes: string;
    actions_taken?: string[];
  }): Promise<{ success: boolean; alert: Alert }> => {
    const response = await apiClient.post(`/alerts/${alertId}/resolve`, data);
    return response.data;
  },

  // Dismiss alert
  dismissAlert: async (alertId: string, reason: string): Promise<{ success: boolean; alert: Alert }> => {
    const response = await apiClient.post(`/alerts/${alertId}/dismiss`, { reason });
    return response.data;
  },

  // Alert Rules
  getRules: async (params?: {
    category?: string;
    severity?: string;
    enabled?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<{ rules: AlertRule[]; total: number }> => {
    const response = await apiClient.get('/alerts/rules', { params });
    return response.data;
  },

  // Get rule by ID
  getRule: async (ruleId: string): Promise<{ success: boolean; rule: AlertRule }> => {
    const response = await apiClient.get(`/alerts/rules/${ruleId}`);
    return response.data;
  },

  // Create alert rule
  createRule: async (data: Partial<AlertRule>): Promise<{ success: boolean; rule: AlertRule }> => {
    const response = await apiClient.post('/alerts/rules', data);
    return response.data;
  },

  // Update alert rule
  updateRule: async (ruleId: string, data: Partial<AlertRule>): Promise<{ success: boolean; rule: AlertRule }> => {
    const response = await apiClient.put(`/alerts/rules/${ruleId}`, data);
    return response.data;
  },

  // Delete alert rule
  deleteRule: async (ruleId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/alerts/rules/${ruleId}`);
    return response.data;
  },

  // Enable/disable rule
  toggleRule: async (ruleId: string, enabled: boolean): Promise<{ success: boolean; rule: AlertRule }> => {
    const response = await apiClient.post(`/alerts/rules/${ruleId}/toggle`, { enabled });
    return response.data;
  },

  // Test alert rule
  testRule: async (ruleId: string): Promise<{
    success: boolean;
    test_result: {
      would_trigger: boolean;
      current_value: any;
      threshold_value: any;
      message: string;
    };
  }> => {
    const response = await apiClient.post(`/alerts/rules/${ruleId}/test`);
    return response.data;
  },

  // Get alert categories
  getCategories: async (): Promise<{ success: boolean; categories: string[] }> => {
    const response = await apiClient.get('/alerts/categories');
    return response.data;
  },

  // Get alert statistics
  getStatistics: async (days: number = 30): Promise<{
    success: boolean;
    statistics: {
      total_alerts: number;
      by_severity: { [key: string]: number };
      by_category: { [key: string]: number };
      by_status: { [key: string]: number };
      trend: Array<{ date: string; count: number }>;
    };
  }> => {
    const response = await apiClient.get(`/alerts/statistics?days=${days}`);
    return response.data;
  },

  // Bulk acknowledge alerts
  bulkAcknowledge: async (alertIds: string[], notes?: string): Promise<{
    success: boolean;
    acknowledged_count: number;
  }> => {
    const response = await apiClient.post('/alerts/bulk-acknowledge', { alert_ids: alertIds, notes });
    return response.data;
  },

  // Bulk resolve alerts
  bulkResolve: async (alertIds: string[], resolutionNotes: string): Promise<{
    success: boolean;
    resolved_count: number;
  }> => {
    const response = await apiClient.post('/alerts/bulk-resolve', {
      alert_ids: alertIds,
      resolution_notes: resolutionNotes,
    });
    return response.data;
  },
};

export default alertsService;

